import {
    Box,
    Button,
    Flex,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Grid,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import { MdAdd, MdClose } from 'react-icons/md';
import { translate } from '../../../i18n';
import { typography } from '../../../theme/tokens';
import {
    PricingBlock,
    SLOT_DURATION_OPTIONS_MINUTES,
    SLOT_INTERVAL_OPTIONS_MINUTES,
    blockRangeMinutes,
    findOverlappingBlockIds,
} from '../model';

type Props = {
    blocks: PricingBlock[];
    currencySymbol: string;
    blockColorFor: (blockId: string) => string;
    onChange: (blockId: string, patch: Partial<PricingBlock>) => void;
    onAdd: () => void;
    onRemove: (blockId: string) => void;
};

const COLUMN_TEMPLATE = { base: '1fr', md: '1fr 1fr 1fr 1.1fr 1.1fr 40px' } as const;

/**
 * The "blocks" half of the pricing grid: one row per block with its hours,
 * slot duration, display interval and optional flat price. Pure UI — every
 * change goes back up through `onChange`.
 */
export default function PricingBlocksEditor({ blocks, currencySymbol, blockColorFor, onChange, onAdd, onRemove }: Props) {
    const overlapping = findOverlappingBlockIds(blocks);
    const rowBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.50');
    const rowBorder = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
    const dangerBorder = useColorModeValue('red.400', 'red.300');
    const mutedText = useColorModeValue('gray.500', 'gray.500');

    const minuteOptions = (options: readonly number[]) =>
        options.map((minutes) => (
            <option key={minutes} value={minutes}>
                {minutes} {translate('courts.pricing_grid.minutes_short')}
            </option>
        ));

    return (
        <Flex direction="column" gap={3}>
            <Text {...typography.caption} color={mutedText}>
                {translate('courts.pricing_grid.blocks_hint')}
            </Text>

            <Grid templateColumns={COLUMN_TEMPLATE} gap={3} display={{ base: 'none', md: 'grid' }} px={1}>
                {(['block_start', 'block_end', 'block_duration', 'block_interval', 'block_price'] as const).map((key) => (
                    <Text key={key} {...typography.formLabel} color={mutedText}>
                        {translate(`courts.pricing_grid.${key}`)}
                    </Text>
                ))}
                <Box />
            </Grid>

            {blocks.map((block) => {
                const hasOverlap = overlapping.has(block.id);
                const range = blockRangeMinutes(block);
                const hasInvalidTime = range === null || range[0] >= range[1];
                const isLast = blocks.length <= 1;
                return (
                    <Box key={block.id}>
                        <Grid
                            templateColumns={COLUMN_TEMPLATE}
                            gap={3}
                            alignItems="center"
                            p={3}
                            bg={rowBg}
                            borderRadius="12px"
                            borderWidth="1.5px"
                            borderColor={hasOverlap || hasInvalidTime ? dangerBorder : rowBorder}
                            borderLeftWidth="4px"
                            borderLeftColor={blockColorFor(block.id)}
                            data-testid={`pricing-block-${block.id}`}
                        >
                            <Input
                                type="time"
                                step={900}
                                value={block.start}
                                aria-label={translate('courts.pricing_grid.block_start')}
                                onChange={(e) => onChange(block.id, { start: e.target.value })}
                            />
                            <Input
                                type="time"
                                step={900}
                                value={block.end === '24:00' ? '23:59' : block.end}
                                aria-label={translate('courts.pricing_grid.block_end')}
                                onChange={(e) => onChange(block.id, { end: e.target.value === '23:59' ? '24:00' : e.target.value })}
                            />
                            <Select
                                value={block.durationMinutes}
                                aria-label={translate('courts.pricing_grid.block_duration')}
                                onChange={(e) => onChange(block.id, { durationMinutes: Number(e.target.value) })}
                            >
                                {minuteOptions(SLOT_DURATION_OPTIONS_MINUTES)}
                            </Select>
                            <Select
                                value={block.intervalMinutes}
                                aria-label={translate('courts.pricing_grid.block_interval')}
                                onChange={(e) => onChange(block.id, { intervalMinutes: Number(e.target.value) })}
                            >
                                {minuteOptions(SLOT_INTERVAL_OPTIONS_MINUTES)}
                            </Select>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none" color={mutedText} fontSize="sm">
                                    {currencySymbol}
                                </InputLeftElement>
                                <Input
                                    inputMode="decimal"
                                    placeholder={translate('courts.pricing_grid.block_price_placeholder')}
                                    value={block.priceRaw}
                                    fontWeight="600"
                                    aria-label={translate('courts.pricing_grid.block_price')}
                                    onChange={(e) => onChange(block.id, { priceRaw: e.target.value })}
                                />
                            </InputGroup>
                            <IconButton
                                aria-label={translate('courts.pricing_grid.remove_block')}
                                icon={<MdClose />}
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                isDisabled={isLast}
                                onClick={() => onRemove(block.id)}
                            />
                        </Grid>
                        {hasOverlap && (
                            <Text {...typography.caption} color="red.500" mt={1} pl={2}>
                                {translate('courts.pricing_grid.block_overlap_warning')}
                            </Text>
                        )}
                        {hasInvalidTime && (
                            <Text {...typography.caption} color="red.500" mt={1} pl={2}>
                                {translate('courts.pricing_grid.block_invalid_time')}
                            </Text>
                        )}
                    </Box>
                );
            })}

            <Box>
                <Button leftIcon={<MdAdd />} size="sm" variant="lightBrand" onClick={onAdd}>
                    {translate('courts.pricing_grid.add_block')}
                </Button>
            </Box>
        </Flex>
    );
}
